<template>

  <TransitionRoot :show="open" as="template">
    <Dialog as="div" class="fixed z-10 inset-0  m-0.5 sm:m-4 md:mx-16 md:my-32 xl:mx-48 xl:my-36 2xl:mx-80 2xl:my-28"
            @close="$emit('close')">
      <div class="flex items-end justify-center p-4 text-center sm:block sm:p-0 h-full max-h-full">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                         leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-95 transition-opacity"/>
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <!--        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>-->
        <TransitionChild as="template" enter="ease-out duration-300"
                         enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                         enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                         leave-from="opacity-100 translate-y-0 sm:scale-100"
                         leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div
              class="h-full flex flex-col bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:align-middle sm:w-full">

            <!-- Close button-->
            <div class="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
              <button
                  class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  type="button"
                  @click="$emit('close')"
              >
                <span class="sr-only">Close</span>
                <XIcon aria-hidden="true" class="h-6 w-6"/>
              </button>
            </div>

            <!--header -->
            <div class="p-4 sm:px-6 flex-grow-0">
              <DialogTitle as="h3"
                           class="flex items-center space-x-2 text-lg font-medium leading-6 text-gray-900"
                           v-html="'Your innovation project named' + (workspace!== null ? ` <strong class='pl-2'>${workspace.name}</strong>`: '')">
              </DialogTitle>
              <p class="text-sm text-gray-500 pb-2"></p>
              <div class="border-b border-gray"></div>
            </div>

            <!--body -->
            <div
                class="px-4 pb-5 sm:px-6 flex-grow h-full max-h-full overflow-y-auto xl:overflow-hidden flex flex-col">
              <!-- Content -->
              <div class="overflow-hidden overflow-y-auto mt-1">

                <main class="p-2 sm:py-3 sm:px-4">
                  <div class="lg:grid lg:grid-cols-12 lg:gap-x-5">
                    <!-- First step -->
                    <div class="space-y-6 sm:px-6 lg:px-0 lg:col-span-12">

                      <section aria-labelledby="innovation-first-step-details-heading">
                        <div class="shadow sm:rounded-md">
                          <div class="bg-white py-6 px-4 sm:p-6">
                            <div>
                              <h2 id="innovation-first-step-details-heading"
                                  class="text-lg leading-6 font-medium text-gray-900">
                                Innovation information
                              </h2>
                              <p class="mt-1 text-sm text-gray-500 sr-only"></p>
                            </div>


                            <div class="mt-6 grid grid-cols-4 gap-6">
                              <div class="col-span-4 sm:col-span-2">
                                <label class="block text-sm font-medium text-gray-700" for="innovation-name">
                                  Innovation name
                                </label>
                                <input v-model="innovationName"
                                       :class="{'border-red-500': hasInnovationNameError}"
                                       id="innovation-name"
                                       autocomplete="cc-innovation-name"
                                       class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                                       type="text"
                                       placeholder="Name your innovation project"/>
                                <p v-show="hasInnovationNameError"
                                   class="mt-2 text-sm text-red-600"
                                   id="innovation-name-error">
                                  This field is required.
                                </p>
                              </div>

                              <div class="col-span-4 sm:col-span-2">
                                <div class="flex justify-between">
                                  <label class="block text-sm font-medium text-gray-700" for="innovation-website">
                                    Company home page
                                  </label>
                                  <span class="text-sm text-gray-500">Optional</span>
                                </div>
                                <div class="mt-1 flex rounded-md shadow-sm">
                                  <span
                                      class="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm"> https://</span>
                                  <input v-model="innovationWebsite"
                                         :class="{'border-red-500': hasInnovationWebsiteError}"
                                         id="innovation-website"
                                         autocomplete="cc-innovation-website"
                                         class="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md focus:ring-primary focus:border-primary sm:text-sm border-gray-300"
                                         type="text"
                                         placeholder="www.my-innovation.com"
                                  />
                                </div>
                                <p v-show="hasInnovationWebsiteError"
                                   class="mt-2 text-sm text-red-600"
                                   id="innovation-website-error">
                                  This field is required.
                                </p>
                              </div>

                              <div class="col-span-4 sm:col-span-2">
                                <div class="flex justify-between">
                                  <label class="block text-sm font-medium text-gray-700" for="innovation-country">
                                    Innovation country
                                  </label>
                                  <span class="text-sm text-gray-500">Optional</span>
                                </div>
                                <v-select v-model="innovationCountry"
                                          :options="countryChoices"
                                          :class="{'in-error': hasInnovationCountryError}"
                                          :placeholder="'Choose a country'"
                                          label="label"
                                          :reduce="n => n.code !== null ? n.code : n"
                                          id="innovation-country"
                                          autocomplete="cc-innovation-country"
                                          class="mt-1 cursor-default">
                                  <template #open-indicator="{ attributes }">
                                    <span :ref="'openIndicator'" :class="'vs__open-indicator'" :role="'presentation'">
                                      <ChevronDownIcon class="h-4 w-4"></ChevronDownIcon>
                                    </span>
                                  </template>
                                </v-select>
                                <p v-show="hasInnovationCountryError"
                                   class="mt-2 text-sm text-red-600"
                                   id="innovation-country-error">
                                  This field is required.
                                </p>
                              </div>


                            </div>
                          </div>

                          <!--                                                    <div class="px-4 py-3 bg-gray-50 text-right sm:px-6">
                                                                                <button type="submit"
                                                                                        class="bg-gray-800 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900">
                                                                                  Save
                                                                                </button>
                                                                              </div>-->
                        </div>
                      </section>

                      <!-- 2nd step -->
                      <section aria-labelledby="innovation-second-step-details-heading">

                        <div class="shadow sm:rounded-md">
                          <div class="bg-white py-6 px-4 sm:p-6">

                            <div>
                              <h2 id="innovation-second-step-details-heading"
                                  class="text-lg leading-6 font-medium text-gray-900">
                                Optional innovation details
                              </h2>
                              <p class="mt-1 text-sm text-gray-500 sr-only"></p>
                            </div>

                            <div class="mt-6 grid grid-cols-4 gap-6">

                              <div class="col-span-4 sm:col-span-2">
                                <div class="flex justify-between">
                                  <label class="block text-sm font-medium text-gray-700" for="business-area">
                                    Business area
                                  </label>
                                  <span class="text-sm text-gray-500 sr-only">Not in the list, add it</span>
                                  <span class="text-sm text-gray-500 sr-only">Optional</span>
                                </div>
                                <v-select v-model="businessArea"
                                          :options="businessAreaChoices"
                                          :class="{'in-error': hasBusinessAreaError}"
                                          :placeholder="'Select one business area or add your own'"
                                          label="label"
                                          :create-option="value => ({ label: value, code: value.trim() })"
                                          :reduce="n => n.code"
                                          taggable
                                          push-tags
                                          id="business-area"
                                          autocomplete="cc-business-area"
                                          class="mt-1 cursor-default">
                                  <template #open-indicator="{ attributes }">
                                    <span :ref="'openIndicator'" :class="'vs__open-indicator'" :role="'presentation'">
                                      <ChevronDownIcon class="h-4 w-4"></ChevronDownIcon>
                                    </span>
                                  </template>
                                </v-select>
                                <p v-show="hasBusinessAreaError"
                                   class="mt-2 text-sm text-red-600"
                                   id="business-area-error">
                                  This field is required.
                                </p>
                              </div>

                              <div class="col-span-4 sm:col-span-2">
                                <div class="flex justify-between">
                                  <label class="block text-sm font-medium text-gray-700" for="global-trends">
                                    What are the global trends you are most interested?
                                  </label>
                                  <span class="text-sm text-gray-500 sr-only">Optional</span>
                                </div>
                                <v-select v-model="globalTrends"
                                          :options="globalTrendsChoices"
                                          :class="{'in-error': hasGlobalTrendsError}"
                                          :placeholder="'Select one or more global trends'"
                                          label="label"
                                          :reduce="n => n.code"
                                          multiple
                                          id="global-trends"
                                          autocomplete="cc-global-trends"
                                          class="mt-1 cursor-default">
                                  <template #open-indicator="{ attributes }">
                                    <span :ref="'openIndicator'" :class="'vs__open-indicator'" :role="'presentation'">
                                      <ChevronDownIcon class="h-4 w-4"></ChevronDownIcon>
                                    </span>
                                  </template>
                                </v-select>
                                <p v-show="hasGlobalTrendsError"
                                   class="mt-2 text-sm text-red-600"
                                   id="global-trends-error">
                                  This field is required.
                                </p>
                              </div>

                              <div class="col-span-4 sm:col-span-2">
                                <div class="flex justify-between">
                                  <label class="block text-sm font-medium text-gray-700" for="first-priority">
                                    What is the #1 priority for you to work with startups?
                                  </label>
                                  <span class="text-sm text-gray-500 sr-only">Not in the list, add it</span>
                                  <span class="text-sm text-gray-500 sr-only">Optional</span>
                                </div>
                                <v-select v-model="firstPriority"
                                          :options="priority1Choices"
                                          :class="{'in-error': hasFirstPriorityError}"
                                          :placeholder="'Select one priority or add your own'"
                                          label="label"
                                          :create-option="value => ({ label: value, code: value.trim() })"
                                          :reduce="n => n.code"
                                          taggable
                                          push-tags
                                          id="first-priority"
                                          autocomplete="cc-first-priority"
                                          class="mt-1 cursor-default">
                                  <template #open-indicator="{ attributes }">
                                    <span :ref="'openIndicator'" :class="'vs__open-indicator'" :role="'presentation'">
                                      <ChevronDownIcon class="h-4 w-4"></ChevronDownIcon>
                                    </span>
                                  </template>
                                </v-select>
                                <p v-show="hasFirstPriorityError"
                                   class="mt-2 text-sm text-red-600"
                                   id="first-priority-error">
                                  This field is required.
                                </p>
                              </div>

                              <div class="col-span-4 sm:col-span-2">
                                <div class="flex justify-between">
                                  <label class="block text-sm font-medium text-gray-700" for="second-priority">
                                    What is the #2 priority for you to work with startups?
                                  </label>
                                  <span class="text-sm text-gray-500 sr-only">Not in the list, add it</span>
                                  <span class="text-sm text-gray-500 sr-only">Optional</span>
                                </div>
                                <v-select v-model="secondPriority"
                                          :options="priority2Choices"
                                          :class="{'in-error': hasSecondPriorityError}"
                                          :placeholder="'Select one priority or add your own'"
                                          label="label"
                                          :create-option="value => ({ label: value, code: value.trim() })"
                                          :reduce="n => n.code"
                                          taggable
                                          push-tags
                                          id="second-priority"
                                          autocomplete="cc-second-priority"
                                          class="mt-1 cursor-default">
                                  <template #open-indicator="{ attributes }">
                                    <span :ref="'openIndicator'" :class="'vs__open-indicator'" :role="'presentation'">
                                      <ChevronDownIcon class="h-4 w-4"></ChevronDownIcon>
                                    </span>
                                  </template>
                                </v-select>
                                <p v-show="hasSecondPriorityError"
                                   class="mt-2 text-sm text-red-600"
                                   id="second-priority-error">
                                  This field is required.
                                </p>
                              </div>


                            </div>
                          </div>
                          <!--                          <div class="px-4 py-3 bg-gray-50 text-right sm:px-6">
                                                      <button type="submit"
                                                              class="bg-gray-800 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900">
                                                        Save
                                                      </button>
                                                    </div>-->
                        </div>
                      </section>


                      <!-- Third step -->
                      <section aria-labelledby="innovation-third-step-details-heading">
                        <div class="shadow sm:rounded-md">
                          <div class="bg-white py-6 px-4 sm:p-6">
                            <div>
                              <h2 id="innovation-third-step-details-heading"
                                  class="text-lg leading-6 font-medium text-gray-900">
                                Any other additional KPIs?
                              </h2>
                              <p class="mt-1 text-sm text-gray-500 sr-only"></p>
                            </div>

                            <div class="mt-6 grid grid-cols-4 gap-6">


                              <div class="col-span-4 sm:col-span-2">
                                <div class="flex justify-between">
                                  <label class="block text-sm font-medium text-gray-700" for="additional-kpis">
                                    Any other additional KPIs?
                                  </label>
                                  <span class="text-sm text-gray-500 sr-only">Not in the list, add it</span>
                                </div>
                                <v-select v-model="additionalKPIs"
                                          :options="additionalKPIsChoices"
                                          :class="{'in-error': hasAdditionalKPIsError}"
                                          :placeholder="'Select one or more KPIs or add your own'"
                                          label="label"
                                          :create-option="value => ({ label: value, code: value.trim() })"
                                          :reduce="n => n.code"
                                          taggable
                                          push-tags
                                          id="additional-kpis"
                                          autocomplete="cc-additional-kpis"
                                          class="mt-1 cursor-default"
                                          multiple>
                                  <template #open-indicator="{ attributes }">
                                    <span :ref="'openIndicator'" :class="'vs__open-indicator'" :role="'presentation'">
                                      <ChevronDownIcon class="h-4 w-4"></ChevronDownIcon>
                                    </span>
                                  </template>
                                </v-select>
                                <p v-show="hasAdditionalKPIsError"
                                   class="mt-2 text-sm text-red-600"
                                   id="additional-kpis-error">
                                  This field is required.
                                </p>
                              </div>


                              <div class="col-span-4 sm:col-span-2">
                                <label class="block text-sm font-medium text-gray-700" for="ideal-format">
                                  What is your ideal framework to work with Startups?
                                </label>
                                <v-select v-model="idealFormats"
                                          :options="idealFormatChoices"
                                          :class="{'in-error': hasIdealFormatsError}"
                                          :placeholder="'Select one or more ideal frameworks'"
                                          label="label"
                                          :reduce="n => n.code"
                                          multiple
                                          id="ideal-format"
                                          autocomplete="cc-ideal-format"
                                          class="mt-1 cursor-default">
                                  <template #open-indicator="{ attributes }">
                                    <span :ref="'openIndicator'" :class="'vs__open-indicator'" :role="'presentation'">
                                      <ChevronDownIcon class="h-4 w-4"></ChevronDownIcon>
                                    </span>
                                  </template>
                                </v-select>
                                <p v-show="hasIdealFormatsError"
                                   class="mt-2 text-sm text-red-600"
                                   id="ideal-format-error">
                                  This field is required.
                                </p>
                              </div>

                              <div class="col-span-4 sm:col-span-2">
                                <label class="block text-sm font-medium text-gray-700" for="targeted-social-impact">
                                  Targeted social impact
                                </label>
                                <v-select v-model="targetedSocialImpact"
                                          :options="targetedSocialImpactChoices"
                                          :class="{'in-error': hasTargetedSocialImpactError}"
                                          :placeholder="'Select one targeted social impact'"
                                          label="label"
                                          :reduce="n => n.code"
                                          id="targeted-social-impact"
                                          autocomplete="cc-targeted-social-impact"
                                          class="mt-1 cursor-default">
                                  <template #open-indicator="{ attributes }">
                                    <span :ref="'openIndicator'" :class="'vs__open-indicator'" :role="'presentation'">
                                      <ChevronDownIcon class="h-4 w-4"></ChevronDownIcon>
                                    </span>
                                  </template>
                                </v-select>
                                <p v-show="hasTargetedSocialImpactError"
                                   class="mt-2 text-sm text-red-600"
                                   id="targeted-social-impact-error">
                                  This field is required.
                                </p>
                              </div>

                              <div class="col-span-4 sm:col-span-2">
                                <div class="flex justify-between">
                                  <label class="block text-sm font-medium text-gray-700" for="innovation-strength">
                                    What are the strengths of your company?
                                  </label>
                                  <span class="text-sm text-gray-500 sr-only">Not in the list, add it</span>
                                </div>
                                <v-select v-model="innovationStrengths"
                                          :options="innovationStrengthChoices"
                                          :class="{'in-error': hasInnovationStrengthsError}"
                                          :placeholder="'Select one or more strengths or add your own'"
                                          label="label"
                                          :create-option="value => ({ label: value, code: value.trim() })"
                                          :reduce="n => n.code"
                                          taggable
                                          push-tags
                                          id="innovation-strength"
                                          autocomplete="cc-innovation-strength"
                                          class="mt-1 cursor-default"
                                          multiple>
                                  <template #open-indicator="{ attributes }">
                                    <span :ref="'openIndicator'" :class="'vs__open-indicator'" :role="'presentation'">
                                      <ChevronDownIcon class="h-4 w-4"></ChevronDownIcon>
                                    </span>
                                  </template>
                                </v-select>
                                <p v-show="hasInnovationStrengthsError"
                                   class="mt-2 text-sm text-red-600"
                                   id="innovation-strength-error">
                                  This field is required.
                                </p>
                              </div>

                              <div class="col-span-4 sm:col-span-2">
                                <div class="flex justify-between">
                                  <label class="block text-sm font-medium text-gray-700" for="dream-scenario">
                                    What is your dream scenario to work with Startups?
                                  </label>
                                  <span class="text-sm text-gray-500">Max. 500 words</span>
                                </div>
                                <textarea v-model="dreamScenario"
                                          :class="{'border-red-500': hasDreamScenarioError}"
                                          placeholder="Describe with your words your perfect scenario"
                                          id="dream-scenario"
                                          autocomplete="cc-dream-scenario"
                                          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"/>
                                <p v-show="hasDreamScenarioError"
                                   class="mt-2 text-sm text-red-600"
                                   id="dream-scenario-error">
                                  This field is required.
                                </p>
                              </div>


                            </div>
                          </div>
                          <!--                          <div class="px-4 py-3 bg-gray-50 text-right sm:px-6">
                                                      <button type="submit"
                                                              class="bg-gray-800 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900">
                                                        Save
                                                      </button>
                                                    </div>-->
                        </div>
                      </section>


                    </div>
                  </div>
                </main>


              </div>
              <!-- End Content -->
            </div>

            <!--footer -->
            <div class="p-4 sm:px-6 flex-grow-0 border-t ">
              <div class="flex items-end justify-end space-x-3 lg:space-x-6">
                <button
                    class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                    type="button"
                    @click="$emit('close')">
                  Close
                </button>
                <button v-if="_hasChanges"
                        ref="addCompanyToListDoneButtonRef"
                        class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-ternary-500 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm hover:bg-ternary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ternary-500"
                        type="button"
                        @click="saveHandle()">
                  Save & Close
                </button>
              </div>
            </div>

          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>


</template>

<script>
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue';
import {ChevronDownIcon, XIcon} from "@heroicons/vue/outline";
import vSelect from 'vue-select'
import _get from "lodash/get";
import localizesCountries from '@/assets/data/countries.json'
import {mapGetters, mapState} from "vuex";
import _isEmpty from "lodash/isEmpty";

export default {
  name: "InnovationProject",
  emits: ['close', 'createInnovationProject'],
  components: {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    vSelect,
    XIcon,
    ChevronDownIcon,
  },
  props: {
    open: {
      type: Boolean,
      default: true,
    },
    workspace: {
      type: Object,
    },
    countryChoices: {
      type: Array,
      default() {
        return [];
      }
    },
    businessAreaChoices: {
      type: Array,
      default() {
        return [
          {label: 'Banking and insurance', code: 'Banking and insurance'},
          {label: 'Construction', code: 'Construction'},
          {label: 'Consumer goods and consumer packaging goods', code: 'Consumer goods and consumer packaging goods'},
          {label: 'Education', code: 'Education'},
          {label: 'Food and agriculture', code: 'Food and agriculture'},
          {label: 'Health and medical care', code: 'Health and medical care'},
          {label: 'Manufacturing', code: 'Manufacturing'},
          {label: 'Material and energy', code: 'Material and energy'},
          {label: 'Media and advertisement', code: 'Media and advertisement'},
          {label: 'Real estate', code: 'Real estate'},
          {label: 'Research and consulting', code: 'Research and consulting'},
          {label: 'Retail', code: 'Retail'},
          {label: 'Security', code: 'Security'},
          {label: 'Transportation and logistic', code: 'Transportation and logistic'},
          {label: 'Travel and tourism', code: 'Travel and tourism'},
        ];
      }
    },

    priority1Choices: {
      type: Array,
      default() {
        return [
          {label: 'Accelerating new technology integration', code: 'Accelerating new technology integration'},
          {label: 'Business Synergy', code: 'Business Synergy'},
          {label: 'Improve internal processes efficiency', code: 'Improve internal processes efficiency'},
          {label: 'Information gathering', code: 'Information gathering'},
          {label: 'Investment ROI', code: 'Investment ROI'},
          {label: 'New service, new product design', code: 'New service, new product design'},
        ];
      }
    },

    priority2Choices: {
      type: Array,
      default() {
        return [
          {label: 'Accelerating new technology integration', code: 'Accelerating new technology integration'},
          {label: 'Business Synergy', code: 'Business Synergy'},
          {label: 'Improve internal processes efficiency', code: 'Improve internal processes efficiency'},
          {label: 'Information gathering', code: 'Information gathering'},
          {label: 'Investment ROI', code: 'Investment ROI'},
          {label: 'New service, new product design', code: 'New service, new product design'},
        ];
      }
    },

    additionalKPIsChoices: {
      type: Array,
      default() {
        return [
          {label: 'Business Synergy', code: 'Business Synergy'},
          {label: 'New service, new product design', code: 'New service, new product design'},
          {label: 'Accelerating new technology integration', code: 'Accelerating new technology integration'},
          {label: 'Investment ROI', code: 'Investment ROI'},
          {label: 'Information gathering', code: 'Information gathering'},
          {label: 'Improve internal processes efficiency', code: 'Improve internal processes efficiency'},
        ];
      }
    },

    innovationStrengthChoices: {
      type: Array,
      default() {
        return [
          {label: 'Agriculture', code: 'Agriculture'},
          {label: 'AI', code: 'AI'},
          {label: 'Biotechnology', code: 'Biotechnology'},
          {label: 'Blockchain', code: 'Blockchain'},
          {label: 'Environment and energy', code: 'Environment and energy'},
          {label: 'Fintech', code: 'Fintech'},
          {label: 'Hardware', code: 'Hardware'},
          {label: 'IOT / Industrial IOT', code: 'IOT / Industrial IOT'},
          {label: 'Manufacturing Knowhow', code: 'Manufacturing Knowhow'},
          {label: 'Material science', code: 'Material science'},
          {label: 'Medical Devices', code: 'Medical Devices'},
          {label: 'Mobility', code: 'Mobility'},
          {label: 'Robotics and software', code: 'Robotics and software'},
        ];
      }
    },

    globalTrendsChoices: {
      type: Array,
      default() {
        return [
          {label: 'Agritech', code: 'Agritech'},
          {label: 'AI', code: 'AI'},
          {label: 'Beauty tech', code: 'Beauty tech'},
          {label: 'Biotech', code: 'Biotech'},
          {label: 'Blockchain', code: 'Blockchain'},
          {label: 'Digital transformation', code: 'Digital transformation'},
          {label: 'Fintech', code: 'Fintech'},
          {label: 'Foodtech', code: 'Foodtech'},
          {label: 'Future of work', code: 'Future of work'},
          {label: 'Healthcare', code: 'Healthcare'},
          {label: 'Industrial IOT', code: 'Industrial IOT'},
          {label: 'Mobility', code: 'Mobility'},
          {label: 'Region development', code: 'Region development '},
          {label: 'Renewable energy', code: 'Renewable energy'},
          {label: 'Smart city', code: 'Smart city'},
          {label: 'Sportech', code: 'Sportech'},
          {label: 'VR/AR and MR', code: 'VR/AR and MR'},
        ];
      }
    },

    idealFormatChoices: {
      type: Array,
      default() {
        return [
          {label: 'Business alliance', code: 'Business alliance'},
          {label: 'Co-development', code: 'Co-development'},
          {label: 'Hackathon execution', code: 'Hackathon execution'},
          {label: 'Ideathon execution', code: 'Ideathon execution'},
          {
            label: 'Improvement of existing products and processes (upgrade to new product, new service or improve internal processes efficiency)',
            code: 'Improvement of existing products and processes (upgrade to new product, new service or improve internal processes efficiency)'
          },
          {label: 'Investment', code: 'Investment'},
          {label: 'Joint research', code: 'Joint research'},
          {label: 'Joint venture', code: 'Joint venture'},
          {label: 'Look for distributer or reseller', code: 'Potential distributor or reseller'},
          {label: 'Looking for resources (tech or idea)', code: 'Looking for resources (tech or idea)'},
          {label: 'M&A', code: 'M&A'},
          {label: 'Networking', code: 'Networking'},
          {label: 'New market development', code: 'New market development'},
          {label: 'Pitch event execution', code: 'Pitch event execution'},
          {label: 'Sale', code: 'Sale'},
          {label: 'Setup innovation lab', code: 'Setup innovation lab'},
        ];
      }
    },

    targetedSocialImpactChoices: {
      type: Array,
      default() {
        return [
          {label: 'Business', code: 'Business'},
          {label: 'Environmental', code: 'Environmental'},
          {label: 'SDGs', code: 'SDGs'},
        ];
      }
    },
  },
  data() {
    return {
      // 1er step
      innovationName: null,
      innovationWebsite: null,
      innovationCountry: null,

      // 2nd step
      businessArea: null,
      globalTrends: null,
      firstPriority: null,
      secondPriority: null,

      // 3rd step
      additionalKPIs: null,
      idealFormats: null,
      targetedSocialImpact: null,
      innovationStrengths: null,
      dreamScenario: null,

      handledSaveAction: false,
      innovationProject: null,

      _hasChanges: false,
      _isLoading: false,
    }
  },
  created() {
    if (!this.countryChoices.length) {
      const locale = this.getCurrentMemberProfile().locale.split('_')[0];
      for (const alpha3 in localizesCountries) {
        this.countryChoices.push({label: localizesCountries[alpha3][locale], code: alpha3});
      }
    }

    this.loadInnovationProject(this.workspace);
  },
  methods: {
    saveHandle() {
      this.handledSaveAction = true;
      let payload = {
        workspace: this.workspace,
      };

      const properties = ['innovationName', 'innovationWebsite', 'innovationCountry', 'businessArea', 'globalTrends', 'firstPriority', 'secondPriority', 'additionalKPIs', 'idealFormats', 'targetedSocialImpact', 'innovationStrengths', 'dreamScenario',]
      // const properties = ['innovationName']
      for (const property of properties) {
        if (this[`has${property.charAt(0).toUpperCase() + property.slice(1)}Error`]) {
          return;
        }

        if (_isEmpty(this[property])) {
          continue;
        }

        if (property === 'innovationWebsite' && !_isEmpty(this[property])) {
          payload[property] = 'https://' + this[property];
          continue;
        }

        payload[property] = this[property];
      }

      let returnedPromise = null;
      if (this.innovationProject === null) {
        returnedPromise = this.$store
            .dispatch("createInnovationProject", payload)
            .then(function (workspace) {
              this.$emit('createInnovationProject');

              return workspace;
            }.bind(this));

      } else {
        returnedPromise = this.$store.dispatch("editInnovationProject", payload);
      }

      returnedPromise.then(function (workspace) {
        // Update the workspace name according to the project name
        if (workspace.name !== workspace.innovationProject.innovationName) {
          const profile = _get(this.profiles, this.workspace.slug, null);
          const isWorkspaceOwner = _get(profile, 'workspaceOwner', false);
          let callback = 'renameWorkspace';
          if (!isWorkspaceOwner) {
            let callback = 'editUserProfile';
          }
          this.$store.dispatch(callback, {
            workspaceSlug: workspace.slug,
            workspaceName: workspace.innovationProject.innovationName,
            noNotification: true,
          });
        }

        this.$emit('close');
      }.bind(this))
    },

    loadInnovationProject(workspace) {
      const innovation = _get(workspace, 'innovationProject', null);

      if (!innovation) {
        return;
      }

      this._isLoading = true;
      this.innovationProject = innovation;

      // 1er step
      this.innovationName = _get(innovation, 'innovationName', null);
      let innovationWebsite = _get(innovation, 'innovationWebsite', null);
      if (innovationWebsite !== null) {
        const regex = /https?:\/\//i;
        innovationWebsite = innovationWebsite.replace(regex, '');
      }
      this.innovationWebsite = innovationWebsite;
      this.innovationCountry = _get(innovation, 'innovationCountry', null);

      // 2nd step
      this.businessArea = _get(innovation, 'businessArea', null);
      this.globalTrends = _get(innovation, 'globalTrends', null);
      this.firstPriority = _get(innovation, 'priority1', null);
      this.secondPriority = _get(innovation, 'priority2', null);

      // 3rd step
      this.additionalKPIs = _get(innovation, 'additionalKPIs', null);
      this.idealFormats = _get(innovation, 'idealFormatForYourInnovation', null);
      this.targetedSocialImpact = _get(innovation, 'targetedSocialImpact', null);
      this.innovationStrengths = _get(innovation, 'strengthOfYourInnovation', null);
      this.dreamScenario = _get(innovation, 'dreamOpenInnovationScenario', null);


      this._isLoading = false;
    },

    ...mapGetters({
      getCurrentMemberProfile: 'getCurrentMemberProfile',
    }),
  },
  computed: {
    hasInnovationNameError() {
      return this.handledSaveAction && _isEmpty(this.innovationName);
    },
    hasInnovationWebsiteError() {
      // return this.handledSaveAction && _isEmpty(this.innovationWebsite);
      return false;
    },
    hasInnovationCountryError() {
      // return this.handledSaveAction && _isEmpty(this.innovationCountry);
      return false;
    },
    hasBusinessAreaError() {
      // return this.handledSaveAction && _isEmpty(this.businessArea);
      return false;
    },
    hasGlobalTrendsError() {
      // return this.handledSaveAction && _isEmpty(this.globalTrends);
      return false;
    },
    hasFirstPriorityError() {
      // return this.handledSaveAction && _isEmpty(this.firstPriority);
      return false;
    },
    hasSecondPriorityError() {
      // return this.handledSaveAction && _isEmpty(this.secondPriority);
      return false;
    },
    hasAdditionalKPIsError() {
      // return this.handledSaveAction && _isEmpty(this.additionalKPIs);
      return false;
    },
    hasIdealFormatsError() {
      // return this.handledSaveAction && _isEmpty(this.idealFormats);
      return false;
    },
    hasTargetedSocialImpactError() {
      // return this.handledSaveAction && _isEmpty(this.targetedSocialImpact);
      return false;
    },
    hasInnovationStrengthsError() {
      // return this.handledSaveAction && _isEmpty(this.innovationStrengths);
      return false;
    },
    hasDreamScenarioError() {
      // return this.handledSaveAction && _isEmpty(this.dreamScenario);
      return false;
    },

    ...mapState({
      profiles: 'profiles',
    }),
  },
  watch: {
    innovationName(newValue, oldValue) {
      if (!this._isLoading) {
        this._hasChanges = true;
      }
    },
    innovationWebsite(newValue, oldValue) {
      if (!this._isLoading) {
        this._hasChanges = true;
      }
    },
    innovationCountry(newValue, oldValue) {
      if (!this._isLoading) {
        this._hasChanges = true;
      }
    },
    businessArea(newValue, oldValue) {
      if (!this._isLoading) {
        this._hasChanges = true;
      }
    },
    globalTrends(newValue, oldValue) {
      if (!this._isLoading) {
        this._hasChanges = true;
      }
    },
    firstPriority(newValue, oldValue) {
      if (!this._isLoading) {
        this._hasChanges = true;
      }
    },
    secondPriority(newValue, oldValue) {
      if (!this._isLoading) {
        this._hasChanges = true;
      }
    },
    additionalKPIs(newValue, oldValue) {
      if (!this._isLoading) {
        this._hasChanges = true;
      }
    },
    idealFormats(newValue, oldValue) {
      if (!this._isLoading) {
        this._hasChanges = true;
      }
    },
    targetedSocialImpact(newValue, oldValue) {
      if (!this._isLoading) {
        this._hasChanges = true;
      }
    },
    innovationStrengths(newValue, oldValue) {
      if (!this._isLoading) {
        this._hasChanges = true;
      }
    },
    dreamScenario(newValue, oldValue) {
      if (!this._isLoading) {
        this._hasChanges = true;
      }
    }
  },
}
</script>

<style scoped>
>>> {
  --vs-dropdown-max-height: 140px;
  --vs-search-input-placeholder-color: #6b7280;
}
</style>